import { ssmHttpService } from "./http-service";

const ROOT_PATH = (process.env.VUE_APP_ROOT_API as string) || "http://localhost:5000/api/";
const API_BASE = ROOT_PATH.endsWith("/") ? ROOT_PATH.slice(0, -1) : ROOT_PATH;

const STORAGE_KEY = "webpush_subscribed_endpoint";

class WebPushClientService {
  private _subscribing = false;

  /**
   * Punto de entrada principal.
   * Llama a esto cuando el usuario está autenticado y el SW está listo.
   * Si ya está suscrito y el endpoint coincide, no hace nada.
   */
  async suscribirSiPermitido(): Promise<void> {
    if (this._subscribing) return;
    if (!this._isSupported()) return;

    const permission = await this._pedirPermiso();
    if (permission !== "granted") return;

    this._subscribing = true;
    try {
      const registration = await navigator.serviceWorker.ready;
      const publicKey = await this._obtenerVapidPublicKey();
      if (!publicKey) return;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this._urlBase64ToUint8Array(publicKey),
      });

      const endpointActual = subscription.endpoint;
      const endpointGuardado = localStorage.getItem(STORAGE_KEY);

      // Evitar re-registrar si ya enviamos este endpoint
      if (endpointGuardado === endpointActual) return;

      const keys = subscription.toJSON().keys as { p256dh: string; auth: string };

      await ssmHttpService.post(
        `${API_BASE}/push/subscribe`,
        {
          endpoint: endpointActual,
          p256dh: keys.p256dh,
          auth: keys.auth,
        },
        false
      );

      localStorage.setItem(STORAGE_KEY, endpointActual);
      console.log("[WebPush] Suscripción registrada en el servidor.");
    } catch (err) {
      console.warn("[WebPush] No se pudo suscribir:", err);
    } finally {
      this._subscribing = false;
    }
  }

  /**
   * Cancela la suscripción push del navegador y la elimina del servidor.
   */
  async desuscribir(): Promise<void> {
    if (!this._isSupported()) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;

      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      localStorage.removeItem(STORAGE_KEY);

      await ssmHttpService.post(
        `${API_BASE}/push/unsubscribe`,
        { endpoint },
        false
      );

      console.log("[WebPush] Suscripción eliminada.");
    } catch (err) {
      console.warn("[WebPush] Error al desuscribir:", err);
    }
  }

  /** Devuelve true si el navegador soporta Web Push */
  isSupported(): boolean {
    return this._isSupported();
  }

  /** Estado del permiso actual */
  getPermissionState(): NotificationPermission | "unsupported" {
    if (!("Notification" in window)) return "unsupported";
    return Notification.permission;
  }

  private _isSupported(): boolean {
    return (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  }

  private async _pedirPermiso(): Promise<NotificationPermission> {
    if (!("Notification" in window)) return "denied";
    if (Notification.permission !== "default") return Notification.permission;
    return await Notification.requestPermission();
  }

  private async _obtenerVapidPublicKey(): Promise<string | null> {
    try {
      const res = await ssmHttpService.get(
        `${API_BASE}/push/vapid-public-key`,
        undefined,
        false
      );
      return (res && res.public_key) ? res.public_key : null;
    } catch (err) {
      console.warn("[WebPush] No se pudo obtener la VAPID public key.");
      return null;
    }
  }

  /** Convierte la VAPID public key de base64url a Uint8Array para la API del navegador */
  private _urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
  }
}

export const webPushClientService = new WebPushClientService();
